// frontend/src/components/TypingEffect.js
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Custom components for ReactMarkdown
const markdownComponents = {
  // Enhanced paragraph handling with better spacing and line break preservation
  p: ({ children }) => (
    <p style={{ 
      marginBottom: '1.2rem',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.6',
      textAlign: 'justify'
    }}>
      {children}
    </p>
  ),
  
  // Handle bullet points with better spacing
  ul: ({ children }) => (
    <ul style={{ 
      marginBottom: '1.2rem', 
      paddingLeft: '1.5rem',
      listStyle: 'disc'
    }}>
      {children}
    </ul>
  ),
  
  // Handle numbered lists with proper formatting
  ol: ({ children }) => (
    <ol style={{ 
      marginBottom: '1.2rem', 
      paddingLeft: '1.5rem',
      listStyle: 'decimal',
      counterReset: 'item'
    }}>
      {children}
    </ol>
  ),
  
  // Improve list item formatting
  li: ({ children, ordered }) => {
    // Check if this is the start of a numbered item (e.g. "1. ", "2. ")
    const isNumberedItem = typeof children === 'string' && /^\d+\.\s/.test(children);
    
    // If it's already a properly formatted list item, render normally
    if (!isNumberedItem) {
      return (
        <li style={{ 
          marginBottom: '0.75rem',
          lineHeight: '1.6'
        }}>
          {children}
        </li>
      );
    }
    
    // For text that looks like a numbered list but isn't properly formatted,
    // convert it to proper list formatting
    const content = children.replace(/^\d+\.\s/, '');
    return (
      <li style={{ 
        marginBottom: '0.75rem',
        lineHeight: '1.6',
        display: 'list-item'
      }}>
        {content}
      </li>
    );
  },
  
  // Handle line breaks
  br: () => <br />,
  
  // Handle emphasis without using **
  strong: ({ children }) => (
    <span style={{ fontWeight: 'bold' }}>{children}</span>
  ),
  em: ({ children }) => (
    <span style={{ fontStyle: 'italic' }}>{children}</span>
  ),
  
  // Handle text nodes to preserve line breaks
  text: ({ children }) => {
    if (typeof children === 'string') {
      // Convert double line breaks to paragraph breaks
      const paragraphs = children.split(/\n\s*\n/);
      if (paragraphs.length > 1) {
        return paragraphs.map((paragraph, index) => (
          <React.Fragment key={index}>
            {paragraph}
            {index < paragraphs.length - 1 && <><br /><br /></>}
          </React.Fragment>
        ));
      }
    }
    return children;
  }
};

// Function to preprocess content for better paragraph formatting
const preprocessContent = (content) => {
  if (!content) return '';
  
  // Convert multiple consecutive line breaks to double line breaks (markdown paragraph separator)
  let processed = content
    // First normalize line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Convert 2+ consecutive newlines to paragraph breaks
    .replace(/\n{2,}/g, '\n\n')
    // Ensure there's proper spacing around formal phrases (but avoid breaking after numbered items)
    .replace(/(\. )([A-ZČĆŽŠĐ][a-zčćžšđ])/g, (match, p1, p2, offset, string) => {
      // Check if this period follows a number (numbered list item)
      const beforePeriod = string.substring(0, offset + 1);
      const hasNumberBefore = /\d+\.\s*$/.test(beforePeriod);
      
      // If it's a numbered item, don't add line break
      if (hasNumberBefore) {
        return match; // Return unchanged
      }
      
      // Otherwise, add paragraph break
      return p1 + '\n\n' + p2;
    })
    // Add line breaks before common formal phrases
    .replace(/(Shodno navedenom|Nadalje|U tom slučaju|Dodatno|Važno je istaći|S poštovanjem)/g, '\n\n$1')
    // Ensure "Poštovani," starts on new line if not at beginning
    .replace(/([^\n])(\s*Poštovani,)/g, '$1\n\n$2')
    // Clean up any triple+ newlines
    .replace(/\n{3,}/g, '\n\n');
    
  return processed;
};

const TypingEffect = ({ content, typingSpeed = 10, markdownEnabled = true }) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  // Preprocess content for better formatting
  const processedContent = preprocessContent(content);
  const contentRef = useRef(processedContent);
  const indexRef = useRef(0);
  const timerRef = useRef(null);
  
  // Reset typing if content changes
  useEffect(() => {
    const newProcessedContent = preprocessContent(content);
    // Check if content has changed
    if (newProcessedContent !== contentRef.current) {
      contentRef.current = newProcessedContent;
      indexRef.current = 0;
      setDisplayedContent('');
      setIsComplete(false);
    }
  }, [content]);
  
  // Handle typing effect
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // If typing is complete, don't set a new timer
    if (isComplete) return;
    
    // Function to add the next character
    const typeNextCharacter = () => {
      // If we've typed all characters, mark as complete
      if (indexRef.current >= contentRef.current.length) {
        setIsComplete(true);
        clearInterval(timerRef.current);
        return;
      }
      
      // Add the next character to displayed content
      setDisplayedContent(prevContent => {
        // Make sure we're starting from the right position
        if (prevContent.length !== indexRef.current) {
          console.warn('Typing effect index mismatch. Resetting.');
          indexRef.current = prevContent.length;
        }
        
        // Get the next character
        const nextChar = contentRef.current.charAt(indexRef.current);
        indexRef.current++;
        
        // Return updated content
        return prevContent + nextChar;
      });
    };
    
    // Set interval for typing
    timerRef.current = setInterval(typeNextCharacter, typingSpeed);
    
    // Clean up timer on unmount or when content changes
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [typingSpeed, isComplete]);
  
  // Create placeholder for consistent height
  const placeholderStyle = {
    visibility: 'hidden',
    position: 'absolute',
    zIndex: -1,
    top: 0,
    left: 0,
    width: '100%'
  };
  
  // Render content
  return (
    <div className="typing-effect-container">
      {/* Invisible placeholder to maintain consistent height */}
      <div style={placeholderStyle}>
        {markdownEnabled ? (
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {contentRef.current}
          </ReactMarkdown>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
            {contentRef.current}
          </div>
        )}
      </div>
      
      {/* Visible typing content */}
      <div className="typing-effect">
        {markdownEnabled ? (
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {displayedContent}
          </ReactMarkdown>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
            {displayedContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default TypingEffect;